import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { Grid, Paper } from "@mui/material";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDDropzone from "components/MDDropzone";

import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import UploadedFilePreview from "components/MDDropzone/UploadedFilePreview";
import { appConfirm } from "components/AppDialog";

import {
    CrmRowActions,
    CrmPageHeader,
    CrmActionButton,
    CrmNotice,
    CrmFormCard,
    CrmTableCard,
    ListLink,
    ListText,
    ListStatus,
} from "../../ecommerce/products/new-product/crm/shared/CrmUi";
function CertificateTable() {
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [notification, setNotification] = useState({
        value: false,
        color: "info",
        text: "",
    });

    const [uploadFile, setUploadFile] = useState([]);
    const [fileName, setFileName] = useState("");
    const [expireDate, setExpireDate] = useState("");
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    const showNotification = useCallback((text, color = "info") => {
        setNotification({ value: true, color, text });
    }, []);

    const loadCertificates = useCallback(async () => {
        try {
            setLoading(true);

            const res = await CrudService.getCertificates();

            const normalized = res.data.map((item) => ({
                id: item.id,
                ...item.attributes,
            }));

            setData(normalized);
        } catch (err) {
            showNotification("Failed to load certificates.", "error");
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        loadCertificates();
    }, [loadCertificates]);

    const totalCertificates = data.length;

    const activeCertificates = data.filter((c) =>
        c.validUntil ? !dayjs(c.validUntil).isBefore(dayjs(), "day") : false
    ).length;

    const expiredCertificates = data.filter((c) =>
        c.validUntil ? dayjs(c.validUntil).isBefore(dayjs(), "day") : false
    ).length;

    const resetForm = () => {
        setUploadFile([]);
        setFileName("");
        setExpireDate("");
    };

    const validateUpload = () => {
        const d = dayjs(expireDate, "YYYY-MM-DD", true);

        if (!fileName.trim()) {
            showNotification("Please enter file name.", "error");
            return false;
        }

        if (!expireDate) {
            showNotification("Please select expire date.", "error");
            return false;
        }

        if (!d.isValid()) {
            showNotification("Expire date is invalid.", "error");
            return false;
        }

        if (d.isBefore(dayjs(), "day")) {
            showNotification("Expire date cannot be in the past.", "error");
            return false;
        }

        if (d.isAfter(dayjs("2100-12-31"), "day")) {
            showNotification("Expire date is too far in the future.", "error");
            return false;
        }

        if (!uploadFile.length) {
            showNotification("Please select a certificate file.", "error");
            return false;
        }

        return true;
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!validateUpload()) return;

        try {
            setUploading(true);

            const formData = new FormData();
            formData.append("fileName", fileName.trim());
            formData.append("expireDate", expireDate);
            formData.append("certificate", uploadFile[0]);

            const res = await CrudService.uploadCertificate(formData);

            setData((prev) => [
                ...prev,
                {
                    id: res.data.id,
                    ...res.data.attributes,
                },
            ]);

            resetForm();
            showNotification("Certificate uploaded successfully.", "success");
        } catch (err) {
            showNotification("Upload failed.", "error");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await appConfirm(
            "Are you sure you want to delete this certificate?"
        );

        if (!confirmed) return;

        try {
            await CrudService.deleteCertificate(id);
            setData((prev) => prev.filter((c) => c.id !== id));
            showNotification("Certificate deleted successfully.", "success");
        } catch (err) {
            showNotification("Failed to delete certificate.", "error");
        }
    };

    const rows = useMemo(
        () =>
            data.map((c) => {
                const exp = c.validUntil ? dayjs(c.validUntil) : null;
                const isExpired = exp?.isBefore(dayjs(), "day");

                return {
                    id: c.id,

                    fileName: (
                        <ListLink onClick={() => navigate(`/certificates/details?id=${c.id}`)}>
                            {c.fileName || "—"}
                        </ListLink>
                    ),

                    uploadedBy:
                        (c.uploadedBy && (c.uploadedBy.name || c.uploadedBy.email)) || "—",

                    expireDate: exp ? (
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <ListText primary={exp.format("DD.MM.YYYY")} />
                            <ListStatus label={isExpired ? "Expired" : "Valid"} tone={isExpired ? "red" : "green"} />
                        </MDBox>
                    ) : (
                        "—"
                    ),

                    uploadedAt: c.created_at
                        ? dayjs(c.created_at).format("YYYY-MM-DD HH:mm")
                        : "—",

                    actions: (
                        <CrmRowActions onEdit={() => navigate(`/certificates/details?id=${c.id}`)} editTitle="Edit Certificate" onDelete={() => handleDelete(c.id)} deleteTitle="Delete Certificate" />
                    ),
                };
            }),
        [data, navigate]
    );

    const tableConfig = {
        columns: [
            { Header: "File Name", accessor: "fileName", disableSortBy: true },
            { Header: "Uploaded By", accessor: "uploadedBy", disableSortBy: true },
            { Header: "Expire Date", accessor: "expireDate", disableSortBy: true },
            { Header: "Uploaded At", accessor: "uploadedAt", disableSortBy: true },
            { Header: "Actions", accessor: "actions", disableSortBy: true },
        ],
        rows,
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={3} pb={3} display="flex" flexDirection="column" gap={2.5}>
                {notification.value && (
                    <CrmNotice tone={notification.color === "error" ? "red" : notification.color === "success" ? "green" : "blue"}>
                        {notification.text}
                    </CrmNotice>
                )}

                <CrmPageHeader
                    title="Certificates"
                    subtitle="Company certificates with their expiry dates. Upload a new file below or open a certificate to edit it."
                    meta={[
                        `${totalCertificates} certificates`,
                        `${activeCertificates} active`,
                        `${expiredCertificates} expired`,
                    ]}
                />

                <CrmFormCard title="Upload certificate" subtitle="Accepted formats: PDF, DOC, DOCX">

                        <MDBox
                            component="form"
                            onSubmit={handleUpload}
                            encType="multipart/form-data"
                        >
                            <Grid container spacing={2.5} alignItems="stretch">
                                <Grid item xs={12} md={3}>
                                    <MDInput
                                        fullWidth
                                        label="File Name"
                                        value={fileName}
                                        onChange={(e) => setFileName(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <MDInput
                                        fullWidth
                                        type="date"
                                        label="Expire Date"
                                        value={expireDate}
                                        onChange={(e) => setExpireDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            min: dayjs().format("YYYY-MM-DD"),
                                            max: "2100-12-31",
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            border: "1px dashed #bdbdbd",
                                            borderRadius: 2,
                                            height: 56,
                                            px: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#fafafa",
                                            overflow: "hidden",
                                            position: "relative",
                                        }}
                                    >
                                        <MDBox
                                            id="certDropzoneHost"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                position: "relative",
                                            }}
                                        >
                                            <MDDropzone
                                                options={{
                                                    addRemoveLinks: false,
                                                    maxFiles: 1,
                                                    acceptedFiles: ".pdf,.doc,.docx",
                                                }}
                                                onChange={(files) => setUploadFile(files)}
                                            />

                                            <MDBox
                                                onClick={() => {
                                                    const host = document.getElementById("certDropzoneHost");
                                                    const input =
                                                        host?.querySelector("input[type='file']") ||
                                                        document.querySelector(".dz-hidden-input");

                                                    input?.click();
                                                }}
                                                sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#fafafa",
                                                }}
                                            >
                                                <MDTypography variant="button" color="text">
                                                    {uploadFile.length
                                                        ? uploadFile[0].name
                                                        : "Choose Certificate"}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </Paper>

                                    {uploadFile.length > 0 && (
                                        <UploadedFilePreview
                                            layout="stack"
                                            file={uploadFile[0]}
                                            onRemove={() => setUploadFile([])}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <MDBox sx={{ "& > button": { width: "100%", height: 56 } }}>
                                        <CrmActionButton type="submit" disabled={uploading} icon={<CloudUploadIcon />}>
                                            {uploading ? "Saving..." : "Upload"}
                                        </CrmActionButton>
                                    </MDBox>
                                </Grid>
                            </Grid>
                        </MDBox>
                </CrmFormCard>

                <CrmTableCard title="Certificate list" summary={`${data.length} certificates · search, edit or delete uploaded files`}>

                        <DataTable
                            table={tableConfig}
                            canSearch
                            entriesPerPage={{ defaultValue: 25, entries: [5, 10, 25, 50] }}
                            showTotalEntries
                            isSorted={false}
                        />

                        {!loading && data.length === 0 && (
                            <MDBox py={5} textAlign="center">
                                <InsertDriveFileIcon sx={{ fontSize: 56, color: "#bdbdbd" }} />

                                <MDTypography display="block" variant="button" color="text" mt={1}>
                                    No certificates found.
                                </MDTypography>
                            </MDBox>
                        )}
                </CrmTableCard>
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
}

export default CertificateTable;